/* Why Choose EverStain: reveal on scroll, launch film, color picker, real-projects gallery + lightbox, count-up. No dependencies. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.dataset.eswReady) return;
    root.dataset.eswReady = '1';
    root.classList.add('esw-js');

    // reveal: stagger siblings, fire once
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -6% 0px' });
    const groups = new Map();
    root.querySelectorAll('.esw-in, [data-esw-slab]').forEach((el) => {
      const k = el.parentElement;
      const i = groups.get(k) || 0;
      groups.set(k, i + 1);
      if (!reduced && el.classList.contains('esw-in')) el.style.transitionDelay = `${Math.min(i, 6) * 90}ms`;
      io.observe(el);
    });

    film(root);
    root.eswProjects = projects(root);
    palette(root);
    countUp(root);
  }

  function film(root) {
    const box = root.querySelector('[data-esw-film]');
    if (!box) return;
    const video = box.querySelector('video');
    const btn = box.querySelector('[data-esw-sound]');
    const label = box.querySelector('[data-esw-sound-label]');
    const mobile = matchMedia('(max-width: 900px)').matches;
    const src = (mobile && box.dataset.srcMobile) || box.dataset.srcDesktop || box.dataset.srcMobile;
    if (mobile && video.dataset.posterMobile) video.poster = video.dataset.posterMobile;
    if (!src) { box.classList.add('is-empty'); return; }
    const start = () => {
      if (video.src) return;
      video.src = src;
      if (!reduced) video.play().catch(() => {});
    };
    // start loading while the film is still up to ~half a screen below the fold, so it is already playing when it scrolls in
    new IntersectionObserver((es, o) => { if (es[0].isIntersecting) { start(); o.disconnect(); } }, { rootMargin: '0px 0px 50% 0px' }).observe(box);
    btn.addEventListener('click', () => {
      start();
      if (video.muted) {
        video.muted = false; video.currentTime = 0; video.play().catch(() => {});
        label.textContent = 'Mute'; btn.querySelector('.esw-film__icon').textContent = '■';
      } else {
        video.muted = true;
        label.textContent = 'Watch with sound'; btn.querySelector('.esw-film__icon').textContent = '▶';
      }
    });
  }

  function palette(root) {
    const pal = root.querySelector('[data-esw-palette]');
    if (!pal) return;
    const swatches = [...pal.querySelectorAll('.esw-swatch')];
    const bgs = [...pal.querySelectorAll('.esw-palette__bg')];
    const jug = pal.querySelector('[data-esw-jug]');
    const name = pal.querySelector('[data-esw-name]');
    const sku = pal.querySelector('[data-esw-sku]');
    const shop = pal.querySelector('[data-esw-shop]');
    const shopName = pal.querySelector('[data-esw-shop-name]');
    const seeit = pal.querySelector('[data-esw-seeit]');
    const gal = root.eswProjects;
    if (seeit && gal) {
      seeit.href = `#${gal.id}`;
      seeit.hidden = !gal.hasColor((pal.querySelector('.esw-swatch.is-on') || {}).dataset?.slug);
      seeit.addEventListener('click', (e) => {
        e.preventDefault();
        const sw = pal.querySelector('.esw-swatch.is-on');
        gal.filterColor(sw.dataset.slug, sw.dataset.name);
      });
    }
    // warm the cache once the section is close
    new IntersectionObserver((es, o) => {
      if (!es[0].isIntersecting) return;
      bgs.forEach((b) => { b.loading = 'eager'; });
      swatches.forEach((s) => { const i = new Image(); i.src = s.dataset.jug; });
      o.disconnect();
    }, { rootMargin: '400px 0px' }).observe(pal);

    let token = 0;
    const select = (sw, focus) => {
      swatches.forEach((s) => { const on = s === sw; s.classList.toggle('is-on', on); s.setAttribute('aria-checked', on); s.tabIndex = on ? 0 : -1; });
      bgs.forEach((b) => b.classList.toggle('is-on', b.dataset.slug === sw.dataset.slug));
      name.textContent = sw.dataset.name;
      shopName.textContent = sw.dataset.name;
      shop.href = sw.dataset.url;
      if (seeit && gal) seeit.hidden = !gal.hasColor(sw.dataset.slug);
      sku.textContent = `SKU ${sw.dataset.sku} · 4 oz · 1 gal · 5 gal`;
      const my = ++token;
      jug.classList.add('is-swap');
      const next = new Image();
      next.src = sw.dataset.jug;
      const swap = () => { if (my !== token) return; jug.src = sw.dataset.jug; jug.alt = `EverStain Acid Stain, 1 gallon, ${sw.dataset.name}`; requestAnimationFrame(() => jug.classList.remove('is-swap')); };
      (next.decode ? next.decode() : Promise.resolve()).then(() => setTimeout(swap, reduced ? 0 : 160), swap);
      if (focus) sw.focus();
    };
    swatches.forEach((sw, i) => {
      sw.tabIndex = sw.classList.contains('is-on') ? 0 : -1;
      sw.addEventListener('click', () => select(sw));
      sw.addEventListener('keydown', (e) => {
        const d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        select(swatches[(i + d + swatches.length) % swatches.length], true);
      });
    });
  }

  function projects(root) {
    const grid = root.querySelector('[data-esw-grid]');
    if (!grid) return null;
    const section = grid.closest('[data-esw-real]');
    const tiles = [...grid.querySelectorAll('.esw-tile[data-zone]')];
    if (!tiles.length) return null;
    const initial = parseInt(grid.dataset.initial, 10) || 8;
    const more = section.querySelector('[data-esw-more]');
    const zones = [...section.querySelectorAll('[data-esw-zone]')];
    const chip = section.querySelector('[data-esw-color-chip]');
    const count = { all: tiles.length, inside: 0, outside: 0 };
    tiles.forEach((t) => { if (t.dataset.zone in count) count[t.dataset.zone] += 1; });
    zones.forEach((b) => { b.hidden = !(count.inside && count.outside); b.querySelector('span').textContent = count[b.dataset.eswZone]; });
    const st = { zone: 'all', color: '', all: false };
    const has = (t, slug) => t.dataset.colors.split(' ').includes(slug);
    const match = (t) => (st.zone === 'all' || t.dataset.zone === st.zone) && (!st.color || has(t, st.color));
    const apply = () => {
      let n = 0;
      tiles.forEach((t) => { const m = match(t); t.hidden = !m || (!st.all && n >= initial); if (m) n += 1; });
      more.hidden = st.all || n <= initial;
      more.querySelector('span').textContent = n;
      zones.forEach((b) => { const on = b.dataset.eswZone === st.zone; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on); });
      chip.hidden = !st.color;
    };
    zones.forEach((b) => b.addEventListener('click', () => { st.zone = b.dataset.eswZone; st.all = false; apply(); }));
    chip.addEventListener('click', () => { st.color = ''; apply(); chip.blur(); });
    more.addEventListener('click', () => {
      const first = tiles.find((t) => t.hidden && match(t));
      st.all = true; apply();
      if (first) first.querySelector('a').focus();
    });

    // lightbox: one native <dialog>, cycles through the projects that match the current filter
    const lb = section.querySelector('[data-esw-lb]');
    const q = (k) => lb.querySelector(`[data-esw-lb-${k}]`);
    const stage = q('stage'); const img = q('img'); const bef = q('before'); const range = q('range');
    const ba = [...lb.querySelectorAll('[data-esw-lb-ba]')];
    const quote = q('quote'); const shopBox = q('shop');
    let list = []; let i = 0; let opener = null;
    const src = (set) => set.split(',')[1].trim().split(' ')[0];
    const show = (k) => {
      i = (k + list.length) % list.length;
      const d = list[i].querySelector('[data-esw-open]').dataset;
      img.srcset = d.srcset; img.sizes = '(max-width: 900px) 100vw, 840px'; img.src = src(d.srcset); img.alt = d.alt;
      const two = Boolean(d.beforeSrcset);
      bef.hidden = !two; range.hidden = !two; ba.forEach((el) => { el.hidden = !two; });
      if (two) { const b = bef.querySelector('img'); b.srcset = d.beforeSrcset; b.sizes = img.sizes; b.src = src(d.beforeSrcset); b.alt = `Before: ${d.alt}`; }
      range.value = 50; stage.style.setProperty('--pos', '50%');
      q('count').textContent = `${i + 1} / ${list.length}`;
      q('title').textContent = d.title;
      q('meta').textContent = d.meta || '';
      quote.hidden = !d.quote;
      if (d.quote) { quote.querySelector('p').textContent = d.quote; quote.querySelector('cite').textContent = d.customer || ''; }
      shopBox.replaceChildren(...(d.shop || '').split(';').filter(Boolean).map((row) => {
        const [name, url, tex] = row.split('|');
        const a = document.createElement('a');
        a.href = url;
        a.innerHTML = '<i></i><span></span><b aria-hidden="true">→</b>';
        if (tex) a.querySelector('i').style.setProperty('--esw-tile', `url('${tex}')`);
        a.querySelector('span').textContent = `Shop ${name}`;
        return a;
      }));
    };
    const close = () => lb.close();
    grid.addEventListener('click', (e) => {
      const a = e.target.closest('[data-esw-open]');
      if (!a || !lb.showModal) return;
      e.preventDefault();
      list = tiles.filter(match); opener = a;
      lb.classList.toggle('is-single', list.length < 2);
      show(list.indexOf(a.closest('.esw-tile')));
      document.documentElement.classList.add('esw-lock');
      lb.showModal();
    });
    lb.addEventListener('close', () => { document.documentElement.classList.remove('esw-lock'); if (opener) opener.focus(); });
    lb.addEventListener('click', (e) => { if (e.target === lb) close(); });
    q('close').addEventListener('click', close);
    q('prev').addEventListener('click', () => show(i - 1));
    q('next').addEventListener('click', () => show(i + 1));
    lb.addEventListener('keydown', (e) => {
      if (e.target === range || list.length < 2) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); }
    });
    range.addEventListener('input', () => stage.style.setProperty('--pos', `${range.value}%`));
    let x0 = null;
    stage.addEventListener('pointerdown', (e) => { x0 = range.hidden ? e.clientX : null; });
    stage.addEventListener('pointerup', (e) => {
      if (x0 === null || list.length < 2) return;
      const dx = e.clientX - x0; x0 = null;
      if (Math.abs(dx) > 40) show(i + (dx < 0 ? 1 : -1));
    });

    apply();
    return {
      id: section.id,
      hasColor: (slug) => Boolean(slug) && tiles.some((t) => has(t, slug)),
      filterColor(slug, name) {
        Object.assign(st, { zone: 'all', color: slug, all: false });
        chip.querySelector('span').textContent = `Color: ${name}`;
        chip.querySelector('i').style.cssText = (root.querySelector(`.esw-swatch[data-slug="${slug}"]`) || {}).style?.cssText || '';
        chip.setAttribute('aria-label', `Showing ${name} projects. Clear color filter`);
        apply();
        section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      },
      reveal(tile) {
        Object.assign(st, { zone: 'all', color: '', all: true });
        apply();
        tile.scrollIntoView({ block: 'center' });
      },
    };
  }

  function countUp(root) {
    root.querySelectorAll('[data-esw-count-to]').forEach((el) => {
      const to = parseInt(el.dataset.eswCountTo, 10);
      if (!to || reduced) return;
      new IntersectionObserver((es, o) => {
        if (!es[0].isIntersecting) return;
        o.disconnect();
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min(1, (now - t0) / 900);
          el.textContent = `${Math.round(to * (1 - Math.pow(1 - p, 3)))}¢`;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.6 }).observe(el);
    });
  }

  const boot = () => document.querySelectorAll('[data-esw]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);   // theme editor
  document.addEventListener('shopify:block:select', (e) => {
    const tile = e.target.closest && e.target.closest('.esw-tile');
    const root = tile && tile.closest('[data-esw]');
    if (root && root.eswProjects) root.eswProjects.reveal(tile);
  });
})();
